import React from 'react';
import { ExtendEstimateWithContext } from '../closeout/closeoutTypes';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { aggregateItemsByName } from '../closeout/extension/aggregationUtils';

export interface Report {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  generate?: (reportOption?: string) => Promise<void>;
  generateForEditor?: (options?: {
    withBreakouts?: boolean;
    reportOption?: string;
  }) => Promise<string>;
  hasOptions?: boolean;
  options?: {
    id: string;
    name: string;
    description: string;
    isDefault: boolean;
  }[];
}

// Helper function to escape CSV values
const escapeCSV = (value: string | number): string => {
  const stringValue = value.toString();
  if (stringValue.search(/("|,|\n)/g) >= 0) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

// Helper function to download a file
const downloadFile = (content: string, filename: string, type: string) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// Helper to format numbers
const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

// Type guard to ensure we have the required properties
const isValidExtendEstimate = (item: any): item is ExtendEstimateWithContext => {
  return (
    item &&
    typeof item === 'object' &&
    'itemname' in item &&
    'attributegroupname' in item &&
    'totalitemqty' in item &&
    'itemuomname' in item &&
    'itemunitcost' in item &&
    'totalcost' in item &&
    'hourlyproductionrate' in item &&
    'totaldirectlaborhours' in item
  );
};

export const generateReports = (data: any[]): Report[] => {
  const validData = data.filter(isValidExtendEstimate);

  return [
    {
      id: 'csv-export',
      name: 'CSV Export',
      description: 'Export extension data to CSV with aggregation options',
      icon: <DescriptionIcon />,
      hasOptions: true,
      options: [
        {
          id: 'aggregated',
          name: 'Aggregated by Name (Default)',
          description: 'Items with same name are combined with totaled quantities',
          isDefault: true,
        },
        {
          id: 'raw',
          name: 'Raw Data',
          description:
            'Individual line items without aggregation, includes breakout and quote group data',
          isDefault: false,
        },
      ],
      generate: async (reportOption?: string) => {
        const selectedOption = reportOption || 'aggregated';

        if (selectedOption === 'raw') {
          // Raw CSV export with additional columns
          let csvContent =
            'Type,Name,Attribute Group,Item Qty,Item UOM,Item Cost Per UOM,Total Material Cost,Hours per Unit,Total Labor Hours,Breakout Name,Quote Group\n';

          // Add data rows for individual items
          validData.forEach(item => {
            const effectiveUnitCost = item.isoverrideitemunitcost
              ? item.overrideitemunitcost
              : item.itemunitcost;
            const effectiveProductionRate = item.isoverridehourlyproductionrate
              ? item.overridehourlyproductionrate
              : item.hourlyproductionrate;

            csvContent += `row,${escapeCSV(item.itemname || '')},${escapeCSV(
              item.attributegroupname || ''
            )},${item.totalitemqty || ''},${escapeCSV(item.itemuomname || '')},${
              effectiveUnitCost || ''
            },${item.totalcost || ''},${effectiveProductionRate || ''},${
              item.totaldirectlaborhours || ''
            },${escapeCSV(item.breakoutname || '')},${escapeCSV(item.quotegroupname || '')}\n`;
          });

          downloadFile(csvContent, 'extension_data_raw.csv', 'text/csv;charset=utf-8;');
        } else {
          // Aggregated CSV export (default)
          const aggregatedItems = aggregateItemsByName(validData, true); // forceAggregateByName = true

          let csvContent =
            'Type,Name,Attribute Group,Item Qty,Item UOM,Item Cost Per UOM,Total Material Cost,Hours per Unit,Total Labor Hours\n';

          // Add data rows for aggregated items
          aggregatedItems.forEach(item => {
            const effectiveUnitCost = item.isoverrideitemunitcost
              ? item.overrideitemunitcost
              : item.itemunitcost;
            const effectiveProductionRate = item.isoverridehourlyproductionrate
              ? item.overridehourlyproductionrate
              : item.hourlyproductionrate;

            csvContent += `row,${escapeCSV(item.itemname || '')},${escapeCSV(
              item.attributegroupname || ''
            )},${item.totalitemqty || ''},${escapeCSV(item.itemuomname || '')},${
              effectiveUnitCost || ''
            },${item.totalcost || ''},${effectiveProductionRate || ''},${
              item.totaldirectlaborhours || ''
            }\n`;
          });

          downloadFile(csvContent, 'extension_data_aggregated.csv', 'text/csv;charset=utf-8;');
        }
      },
    },
    {
      id: 'quoted-items',
      name: 'Quoted Items by Quote Group',
      description:
        'Generate an editable bill of materials organized by quote groups for vendor quoting',
      icon: <GroupWorkIcon />,
      hasOptions: true,
      options: [
        {
          id: 'no-breakouts',
          name: 'No Breakouts (Default)',
          description: 'Aggregated quantities by quote group',
          isDefault: true,
        },
        {
          id: 'with-breakouts',
          name: 'With Breakouts',
          description: 'Organized by quote groups and breakouts',
          isDefault: false,
        },
        {
          id: 'no-aggregation',
          name: 'No Aggregation',
          description: 'Individual line items without grouping',
          isDefault: false,
        },
      ],
      generateForEditor: async (
        options: { withBreakouts?: boolean; reportOption?: string } = {}
      ) => {
        const quotedItems = validData.filter(item => item.isquoted);

        let htmlContent = `<h1>Bill of Materials</h1>`;

        if (quotedItems.length === 0) {
          htmlContent += `<p><em>No quoted items found in the current data.</em></p>`;
          return htmlContent;
        }

        const reportOption = options.reportOption || 'no-breakouts';

        if (reportOption === 'no-aggregation') {
          // No aggregation - show individual line items
          htmlContent += `<h2>All Quoted Items (Individual Line Items)</h2>`;
          htmlContent += `
            <table style="width: 100%; border-collapse: separate; border-spacing: 0 8px;">
              <tbody>
                ${quotedItems
                  .map((item, index) => {
                    const cleanUOM = (item.itemuomname || '').replace(/\s*\([^)]*\)/g, '');
                    const quoteGroup = item.quotegroupname || 'Ungrouped';
                    const breakout = item.breakoutname || 'No Breakout';
                    return `
                    <tr style="background-color: ${index % 2 === 0 ? '#ffffff' : '#f9f9f9'};">
                      <td style="padding: 16px; border: 1px solid #e0e0e0; border-radius: 4px;">
                        <div style="font-family: Arial, sans-serif;">
                          <strong style="font-weight: 700;">${formatNumber(item.totalitemqty || 0)} ${cleanUOM}:</strong> ${escapeCSV(item.itemname || '')}
                          <br><small style="color: #666;">Quote Group: ${escapeCSV(quoteGroup)} | Breakout: ${escapeCSV(breakout)}</small>
                        </div>
                      </td>
                    </tr>
                  `;
                  })
                  .join('')}
              </tbody>
            </table>
          `;
        } else {
          // Group items first by quote group
          const groupedItems = quotedItems.reduce(
            (acc, item) => {
              const groupName = item.quotegroupname || 'Ungrouped';
              if (!acc[groupName]) {
                acc[groupName] = [];
              }
              acc[groupName].push(item);
              return acc;
            },
            {} as Record<string, ExtendEstimateWithContext[]>
          );

          // Process each quote group
          Object.entries(groupedItems).forEach(([groupName, items]) => {
            htmlContent += `<div class="quote-group"><h2>${escapeCSV(groupName)}</h2>`;

            if (reportOption === 'with-breakouts') {
              // Further group by breakout within each quote group
              const breakoutGroups = items.reduce(
                (acc, item) => {
                  const breakoutName = item.breakoutname || 'No Breakout';
                  if (!acc[breakoutName]) {
                    acc[breakoutName] = [];
                  }
                  acc[breakoutName].push(item);
                  return acc;
                },
                {} as Record<string, ExtendEstimateWithContext[]>
              );

              // Generate tables for each breakout
              Object.entries(breakoutGroups).forEach(([breakoutName, breakoutItems]) => {
                htmlContent += `
                  <h3>${escapeCSV(breakoutName)}</h3>
                  <table style="width: 100%; border-collapse: separate; border-spacing: 0 8px;">
                    <tbody>
                      ${breakoutItems
                        .map((item, index) => {
                          const cleanUOM = (item.itemuomname || '').replace(/\s*\([^)]*\)/g, '');
                          return `
                          <tr style="background-color: ${index % 2 === 0 ? '#ffffff' : '#f9f9f9'};">
                            <td style="padding: 16px; border: 1px solid #e0e0e0; border-radius: 4px;">
                              <div style="font-family: Arial, sans-serif;">
                                <strong style="font-weight: 700;">${formatNumber(item.totalitemqty || 0)} ${cleanUOM}:</strong> ${escapeCSV(item.itemname || '')}
                              </div>
                            </td>
                          </tr>
                        `;
                        })
                        .join('')}
                    </tbody>
                  </table>
                `;
              });
            } else {
              // No breakouts - aggregate items with the same name
              const aggregatedItems = aggregateItemsByName(items, true); // forceAggregateByName = true

              htmlContent += `
                <table style="width: 100%; border-collapse: separate; border-spacing: 0 8px;">
                  <tbody>
                    ${aggregatedItems
                      .map((item, index) => {
                        const cleanUOM = (item.itemuomname || '').replace(/\s*\([^)]*\)/g, '');
                        return `
                        <tr style="background-color: ${index % 2 === 0 ? '#ffffff' : '#f9f9f9'};">
                          <td style="padding: 16px; border: 1px solid #e0e0e0; border-radius: 4px;">
                            <div style="font-family: Arial, sans-serif;">
                              <strong style="font-weight: 700;">${formatNumber(item.totalitemqty || 0)} ${cleanUOM}:</strong> ${escapeCSV(item.itemname || '')}
                            </div>
                          </td>
                        </tr>
                      `;
                      })
                      .join('')}
                  </tbody>
                </table>
              `;
            }

            htmlContent += `</div>`;
          });
        }

        return htmlContent;
      },
    },
  ];
};

export const createChangeOrderReports = (isEstimateContext: boolean): Report[] => [
  {
    id: 'change-order-or-estimate-report',
    name: isEstimateContext ? 'Estimate Report' : 'Change Order Report',
    description: `Comprehensive ${isEstimateContext ? 'estimate' : 'change order'} report with detailed breakdowns of all cost categories`,
    icon: <AssignmentIcon />,
    generate: async () => {
      // Create and show the enhanced change order report dialog
      const event = new CustomEvent('openChangeOrderDialog', {
        detail: { reportType: 'enhanced' },
      });
      window.dispatchEvent(event);
    },
    hasOptions: false,
  },
];
